<script setup lang="ts">
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { ref, computed } from "vue";

import EventTypesConfiguration from "./EventTypesConfiguration.vue";
import AppearancePicker from "../../Appearances/components/AppearancePicker.vue";
import type { EventTypeGroup } from "../EventTypes.vue";
import AppearancePreview from "../../Appearances/components/AppearancePreview.vue";

import route from "ziggy-js";
import type { IAppearance } from "@/types/IAppearance";
import AvatarUpload from "@/components/ui/avatar-upload/AvatarUpload.vue";

import { useForm, router } from "@inertiajs/vue3";
import Description from "../../EventTypes/components/BookingPage/components/Description.vue";
import GridIntegrationCode from "./GridIntegrationCode.vue";
import Input from "@/components/ui/input/Input.vue";
import { Eye, Code2, Link, ClipboardCheck } from "lucide-vue-next";
import { useClipboard } from "@vueuse/core";

const opened = ref(false);

const props = defineProps<{
    eventTypes: any[];
    eventTypeGroup: EventTypeGroup;
    appearances: IAppearance[];
}>();

const selectedAppearance = computed(() => {
    return props.appearances.find(
        (appearance) => appearance.id === props.eventTypeGroup.appearance_id,
    );
});

type Form = {
    title: string;
    description: string;
    avatar?: string;
    _method: string;
};

const form = useForm<Form>({
    title: props.eventTypeGroup.title,
    description: props.eventTypeGroup.description,
    avatar: props.eventTypeGroup.avatar,
    _method: "put", //For why this is included @see https://inertiajs.com/file-uploads#multipart-limitations
});

function onAppearanceSelected(apperance: IAppearance) {
    router.post(
        route("app.event-type-groups.appearances.assign", {
            eventTypeGroup: props.eventTypeGroup.id,
        }),
        {
            appearanceId: apperance.id,
        },
    );
}

function onTextsSaved() {
    form.post(
        route("app.event-type-groups.update", {
            eventTypeGroup: props.eventTypeGroup.id,
        }),
        {
            onSuccess: () => {
                opened.value = false;
            },
        },
    );
}
const { copy, copied } = useClipboard();

function copyGroupUrl() {
    copy(
        "https://my.zaptime.app/public-event-type-group/" +
            props.eventTypeGroup.uuid,
    );
}
</script>

<template>
    <Dialog v-model:open="opened">
        <DialogTrigger>
            <slot />
        </DialogTrigger>
        <DialogContent class="max-w-lg">
            <DialogHeader>
                <DialogTitle>
                    {{ $t("event_types.group.modal_title") }}</DialogTitle
                >
                <DialogDescription>
                    <div class="flex justify-between">
                        <p>
                            {{ $t("event_types.group.modal_description") }}
                        </p>
                    </div>
                </DialogDescription>
            </DialogHeader>

            <Tabs default-value="texts" class="mt-4">
                <TabsList>
                    <TabsTrigger value="texts">
                        {{ $t("event_types.group.general") }}
                    </TabsTrigger>
                    <TabsTrigger value="event-types-configuration">
                        {{ $t("event_types.group.order") }}
                    </TabsTrigger>
                </TabsList>
                <TabsContent class="min-h-[400px]" value="texts">
                    <form @submit.prevent="onTextsSaved">
                        <div class="mt-4 flex flex-col gap-y-4">
                            <div class="grid w-full items-center gap-1.5">
                                <Label for="avatar">Avatar</Label>
                                <AvatarUpload
                                    :image-url="form.avatar"
                                    :upload-url="route('app.images.store')"
                                    @file-uploaded-json="
                                        (url) => (form.avatar = url)
                                    "
                                ></AvatarUpload>
                            </div>

                            <div class="grid w-full items-center gap-1.5">
                                <Label for="title">{{
                                    $t("event_types.group.title")
                                }}</Label>
                                <Input
                                    id="title"
                                    v-model="form.title"
                                    class="max-w-[436px]"
                                    type="text"
                                    placeholder="Title"
                                />
                            </div>

                            <div class="grid w-full gap-1.5">
                                <Label for="description">{{
                                    $t("event_types.group.description")
                                }}</Label>

                                <Description
                                    v-model="form.description"
                                ></Description>
                            </div>
                        </div>

                        <Label class="mt-4">{{
                            $t("event_types.group.appearance")
                        }}</Label>
                        <div class="mt-2 flex gap-6">
                            <AppearancePicker
                                :selected-appearance="selectedAppearance"
                                :appearances="appearances"
                                @on-selected="onAppearanceSelected"
                            />
                        </div>

                        <Label class="mt-4">{{
                            $t("event_types.group.share")
                        }}</Label>

                        <div class="mt-2 flex gap-2">
                            <Button
                                target="_blank"
                                :href="
                                    '/public-event-type-group/' +
                                    eventTypeGroup.uuid
                                "
                                as="a"
                                class="gap-2"
                                variant="outline"
                                size="sm"
                            >
                                <Eye :size="20" />

                                {{ $t("event_types.group.show") }}
                            </Button>

                            <Button
                                @click.prevent="copyGroupUrl"
                                class="gap-2"
                                variant="outline"
                                size="sm"
                            >
                                <Link v-if="!copied" :size="20" />
                                <ClipboardCheck v-else :size="20" />

                                {{ $t("event_types.group.copy") }}
                            </Button>

                            <div class="">
                                <GridIntegrationCode
                                    :token="eventTypeGroup.token"
                                >
                                    <Button
                                        class="gap-2"
                                        size="sm"
                                        variant="outline"
                                    >
                                        <Code2 :size="20" />
                                        {{
                                            $t(
                                                "event_types.group.ingeration_code",
                                            )
                                        }}
                                    </Button>
                                </GridIntegrationCode>
                            </div>
                        </div>
                    </form>
                </TabsContent>
                <TabsContent class="min-h-[400px]" value="appearance">
                </TabsContent>
                <TabsContent
                    class="min-h-[400px]"
                    value="event-types-configuration"
                >
                    <EventTypesConfiguration
                        :event-types="eventTypes"
                        :event-type-group="eventTypeGroup"
                    ></EventTypesConfiguration>
                </TabsContent>
            </Tabs>

            <DialogFooter class="mt-5">
                <Button @click.prevent="onTextsSaved">{{
                    $t("event_types.group.save")
                }}</Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>
